import React, { memo, useEffect } from 'react'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from 'reactflow'
import { nodeCardClass, nodeChipClass, nodeHandleClass, nodeIcon, nodeTextClass } from '../../nodeCatalog'

const TYPE = 'abTest'
const Icon = nodeIcon(TYPE)

const ABTestNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const variants = data.ab_test_config?.variants || [
    { id: 'variant_1', label: 'Variant 1', percentage: 50 },
    { id: 'variant_2', label: 'Variant 2', percentage: 50 },
  ]

  // Each variant slot needs room for its "V# (##%)" tag; grow the node instead
  // of letting absolutely-positioned labels overlap when there are 3+ variants.
  const nodeMinWidth = Math.max(220, variants.length * 64)

  // ReactFlow caches each node's handles; without this, a variant added in the
  // config panel renders a handle ReactFlow doesn't know about, so edges drawn
  // from it are silently dropped and the variant ends up unwired at runtime.
  const updateNodeInternals = useUpdateNodeInternals()
  const variantKey = variants.map((v: any) => v.id).join(',')
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, variantKey, updateNodeInternals])

  return (
    <div
      className={nodeCardClass(TYPE, selected)}
      style={{ minWidth: nodeMinWidth }}
    >
      <Handle
        type="target"
        position={Position.Top}
        className={nodeHandleClass(TYPE)}
      />
      <div className="flex items-center gap-2">
        <div className={nodeChipClass(TYPE)}>
          <Icon className={`w-4 h-4 ${nodeTextClass(TYPE)}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-emphasis uppercase tracking-wider ${nodeTextClass(TYPE)}`}>A/B Test</p>
          <p className="text-sm font-medium text-foreground truncate">{data.label || 'Split Test'}</p>
        </div>
      </div>
      {/* Variant outputs — labels above, dots on the bottom edge, aligned. A split is
          symmetric: no variant is the pass or fail arm, so all share the node tone. */}
      <div className="relative mt-4 h-4">
        {variants.map((variant: any, index: number) => (
          <span
            key={variant.id}
            className={`absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-emphasis uppercase tracking-wide ${nodeTextClass(TYPE)}`}
            style={{ left: `${((index + 0.5) / variants.length) * 100}%` }}
            title={variant.label}
          >
            V{index + 1} ({variant.percentage}%)
          </span>
        ))}
      </div>
      {variants.map((variant: any, index: number) => (
        <Handle
          key={variant.id}
          type="source"
          position={Position.Bottom}
          id={variant.id}
          className={nodeHandleClass(TYPE)}
          style={{ left: `${((index + 0.5) / variants.length) * 100}%` }}
        />
      ))}
    </div>
  )
}

export default memo(ABTestNode)
